import React, { useState, useMemo, useRef } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import styled from "@emotion/styled";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  VStack,
  HStack,
  Badge,
  Select,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tooltip,
  Avatar,
  useToast,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { MdRefresh, MdCode, MdLock } from "react-icons/md";
import { FaTowerBroadcast } from "react-icons/fa6";
import { motion } from "framer-motion";
import useAxios from "axios-hooks";
import { useRecoilValue } from "recoil";
import Card from "components/card/Card";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { loggedInUserState } from "atoms/loggedInUserState";
import { onlyUAdmins } from "utils/accessLevels";

const GlassMorphismCard = styled(Card)`
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
`;

const MotionTr = motion(Tr);

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => (
  <Button
    size="sm"
    variant={isActive ? "solid" : "outline"}
    colorScheme={isActive ? "orange" : "gray"}
    isDisabled={isDisabled}
    onClick={onClick}
    minW="40px"
    h="40px"
    borderRadius="md"
    _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
    transition="all 0.2s"
  >
    {children}
  </Button>
);

const locationColumns = [
  { Header: "S.No" },
  { Header: "Name" },
  { Header: "City" },
  { Header: "State" },
  { Header: "ID" },
  { Header: "OCPI Enabled" },
  { Header: "Actions" },
];

const OcpiLocationsTable = ({
  locations,
  refetchLocations,
  locListLoading,
  handleBroadcastAll,
  allLocLoading,
  handleBroadcastRow,
  handleViewBody,
  rowBroadcasting,
}) => {
  const toast = useToast();
  const loggedInUser = useRecoilValue(loggedInUserState);
  const canEdit = onlyUAdmins.includes(loggedInUser?.role);
  const [locationSearch, setLocationSearch] = useState("");
  const [ocpiFilter, setOcpiFilter] = useState("all");
  const [updatingLocationId, setUpdatingLocationId] = useState(null);
  const [confirmState, setConfirmState] = useState({
    open: false,
    location: null,
    nextValue: false,
  });
  const cancelRef = useRef();

  const [, executeLocationUpdate] = useAxios({}, { manual: true });

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const locColumns = useMemo(() => locationColumns, []);
  const locData = useMemo(() => {
    if (ocpiFilter === "enabled") {
      return locations.filter((loc) => loc.IsOcpiEnabled);
    }
    if (ocpiFilter === "disabled") {
      return locations.filter((loc) => !loc.IsOcpiEnabled);
    }
    return locations;
  }, [locations, ocpiFilter]);

  const locTableInstance = useTable(
    {
      columns: locColumns,
      data: locData,
      initialState: { pageIndex: 0, pageSize: 10 },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") return rows;
        const q = query.toLowerCase().trim();
        return rows.filter((row) => {
          const o = row.original;
          return (
            (o.Name || "").toLowerCase().includes(q) ||
            (o.City || "").toLowerCase().includes(q) ||
            (o.State || "").toLowerCase().includes(q) ||
            String(o.Id ?? "").includes(q)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const handleLocSearchChange = (e) => {
    const v = e.target.value;
    setLocationSearch(v);
    locTableInstance.setGlobalFilter(v);
  };

  const closeConfirm = () =>
    setConfirmState({ open: false, location: null, nextValue: false });

  const applyLocationUpdate = async (location, nextValue) => {
    setUpdatingLocationId(location.Id);
    try {
      await executeLocationUpdate({
        method: "PATCH",
        url: `/api/locations/update/${location.Id}`,
        data: { IsOcpiEnabled: nextValue },
      });
      toast({
        title: `OCPI ${nextValue ? "enabled" : "disabled"} for location ${
          location.Name || location.Id
        }`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      refetchLocations();
    } catch (err) {
      toast({
        title: "Error updating location OCPI setting",
        description:
          err?.response?.data?.error || err?.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingLocationId(null);
    }
  };

  const handleToggle = (location, nextValue) => {
    if (location.IsOcpiEnabled && !nextValue) {
      setConfirmState({ open: true, location, nextValue });
      return;
    }
    applyLocationUpdate(location, nextValue);
  };

  const handleConfirm = async () => {
    const { location, nextValue } = confirmState;
    closeConfirm();
    if (location) {
      await applyLocationUpdate(location, nextValue);
    }
  };

  const renderPagination = () => {
    const {
      page,
      state: { pageIndex, pageSize },
      canPreviousPage,
      canNextPage,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize: setPS,
    } = locTableInstance;

    return (
      <Flex
        justify="space-between"
        align="center"
        px={6}
        py={4}
        borderTop="1px solid"
        borderColor={borderColor}
        flexWrap="wrap"
        gap={3}
      >
        <HStack spacing={3}>
          <Select
            size="sm"
            value={pageSize}
            onChange={(e) => setPS(Number(e.target.value))}
            borderRadius="md"
            w="120px"
          >
            {[5, 10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Select>
          <Text fontSize="sm" color="gray.500">
            Showing {page.length} of {locData.length} results
          </Text>
        </HStack>

        <HStack spacing={2}>
          <PaginationButton isDisabled={!canPreviousPage} onClick={() => gotoPage(0)}>
            <ChevronLeftIcon boxSize={4} mr={1} />
            First
          </PaginationButton>
          <PaginationButton isDisabled={!canPreviousPage} onClick={previousPage}>
            <ChevronLeftIcon boxSize={4} />
          </PaginationButton>

          {pageCount <= 5 ? (
            Array.from({ length: pageCount }, (_, i) => (
              <PaginationButton key={i} isActive={i === pageIndex} onClick={() => gotoPage(i)}>
                {i + 1}
              </PaginationButton>
            ))
          ) : (
            <>
              {pageIndex > 1 && (
                <PaginationButton onClick={() => gotoPage(0)}>1</PaginationButton>
              )}
              {pageIndex > 2 && <Text color="gray.400">...</Text>}
              {pageIndex > 0 && (
                <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                  {pageIndex}
                </PaginationButton>
              )}
              <PaginationButton isActive>{pageIndex + 1}</PaginationButton>
              {pageIndex < pageCount - 1 && (
                <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                  {pageIndex + 2}
                </PaginationButton>
              )}
              {pageIndex < pageCount - 3 && <Text color="gray.400">...</Text>}
              {pageIndex < pageCount - 2 && (
                <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                  {pageCount}
                </PaginationButton>
              )}
            </>
          )}

          <PaginationButton isDisabled={!canNextPage} onClick={nextPage}>
            <ChevronRightIcon boxSize={4} />
          </PaginationButton>
          <PaginationButton isDisabled={!canNextPage} onClick={() => gotoPage(pageCount - 1)}>
            Last
            <ChevronRightIcon boxSize={4} ml={1} />
          </PaginationButton>
        </HStack>
      </Flex>
    );
  };

  return (
    <GlassMorphismCard>
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          <VStack align="start" spacing={2} width={{ base: "100%", md: "auto" }}>
            <Text fontSize="xs" fontWeight="medium" color="gray.500">
              Search Locations
            </Text>
            <HStack spacing={10}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, city, state, ID..."
                  value={locationSearch}
                  onChange={handleLocSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: "#f57d36",
                    boxShadow: "0 0 0 1px #f57d36",
                  }}
                />
              </InputGroup>
              <Select
                size="sm"
                value={ocpiFilter}
                onChange={(e) => setOcpiFilter(e.target.value)}
                borderRadius="md"
                w="170px"
              >
                <option value="all">All OCPI statuses</option>
                <option value="enabled">OCPI Enabled</option>
                <option value="disabled">OCPI Disabled</option>
              </Select>
              <Button
                leftIcon={<Icon as={MdRefresh} />}
                size="sm"
                borderRadius="md"
                variant="ghost"
                color="#f57d36"
                onClick={() => refetchLocations()}
                isLoading={locListLoading}
                loadingText="Refreshing..."
                aria-label="Refresh locations"
                fontWeight="500"
              >
                Refresh
              </Button>
              <RoleBasedRender roles={onlyUAdmins}>
                <Button
                  leftIcon={<Icon as={FaTowerBroadcast} boxSize={4} />}
                  onClick={() => handleBroadcastAll("locations")}
                  isLoading={allLocLoading}
                  loadingText="Broadcasting..."
                  size="sm"
                  height="36px"
                  minW="160px"
                  px={6}
                  bgGradient="linear(135deg, #f57d36 0%, #f35626 100%)"
                  color="white"
                  borderRadius="8px"
                  boxShadow="0 4px 12px rgba(245, 125, 54, 0.3)"
                  fontWeight="600"
                  transition="all 0.2s ease"
                  _hover={{
                    bgGradient: "linear(135deg, #f35626 0%, #f57d36 100%)",
                    boxShadow: "0 6px 16px rgba(245, 125, 54, 0.4)",
                    transform: "translateY(-2px)",
                  }}
                  _active={{
                    transform: "translateY(0)",
                    boxShadow: "0 2px 8px rgba(245, 125, 54, 0.3)",
                  }}
                >
                  Broadcast All
                </Button>
              </RoleBasedRender>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
              <Icon as={InfoIcon} boxSize={3} mr={1} />
              Type any keyword to search across all fields
            </Text>
          </VStack>

          <Flex align="center" justify="flex-end">
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Locations
            </Text>
            <Badge ml={2} colorScheme="orange" borderRadius="full" px={2}>
              {locations.length} total
            </Badge>
          </Flex>
        </Flex>
      </Box>

      {locListLoading ? (
        <Flex justify="center" py={10}>
          <Spinner color="#f57d36" size="lg" />
        </Flex>
      ) : locData.length === 0 ? (
        <Flex justify="center" py={10}>
          <Text color="gray.400" fontSize="sm">
            {locations.length === 0
              ? "No locations found."
              : "No locations match the selected OCPI status."}
          </Text>
        </Flex>
      ) : (
        <>
          <TableContainer>
            <Table {...locTableInstance.getTableProps()} variant="simple" colorScheme="gray">
              <Thead bg={theadBgColor}>
                {locTableInstance.headerGroups.map((hg, hgIdx) => (
                  <Tr {...hg.getHeaderGroupProps()} key={hgIdx}>
                    {hg.headers.map((column, colIdx) => (
                      <Th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={colIdx}
                        py={4}
                        px={6}
                        borderColor={borderColor}
                        textTransform="uppercase"
                        fontSize="xs"
                        fontWeight="bold"
                        letterSpacing="wider"
                        color="gray.500"
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ color: "#f57d36" }}
                        transition="all 0.2s"
                      >
                        <Flex justify="center" align="center" gap={1}>
                          {column.render("Header")}
                          <Box>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon as={FaSortDown} />
                              ) : (
                                <Icon as={FaSortUp} />
                              )
                            ) : (
                              <Icon as={FaSort} opacity={0.3} />
                            )}
                          </Box>
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody {...locTableInstance.getTableBodyProps()}>
                {locTableInstance.page.map((row, rowIdx) => {
                  locTableInstance.prepareRow(row);
                  const loc = row.original;
                  return (
                    <MotionTr
                      {...row.getRowProps()}
                      key={row.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: rowIdx * 0.03 }}
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                    >
                      {row.cells.map((cell, cellIdx) => {
                        let cellData;
                        if (cell.column.Header === "S.No") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm" fontWeight="600">
                                {locTableInstance.state.pageIndex * locTableInstance.state.pageSize + rowIdx + 1}
                              </Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Name") {
                          const initials = (loc.Name || "")
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .substring(0, 2);
                          cellData = (
                            <Flex align="center" gap={3}>
                              <Avatar
                                name={loc.Name}
                                size="sm"
                                bg="#f57d36"
                                color="white"
                                fontWeight="bold"
                                getInitials={() => initials}
                              />
                              <Box>
                                <Text color={textColor} fontWeight="semibold" fontSize="sm">
                                  {loc.Name || "—"}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {[loc.City, loc.State].filter(Boolean).join(", ") || "—"}
                                </Text>
                              </Box>
                            </Flex>
                          );
                        } else if (cell.column.Header === "City") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm">{loc.City || "—"}</Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "State") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm">{loc.State || "—"}</Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "ID") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Badge
                                bg="#f57d36"
                                color="white"
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontWeight="bold"
                              >
                                {loc.Id}
                              </Badge>
                            </Flex>
                          );
                        } else if (cell.column.Header === "OCPI Enabled") {
                          cellData = (
                            <Flex align="center" justify="center" gap={2}>
                              {canEdit && (
                                <Switch
                                  colorScheme="orange"
                                  isChecked={!!loc.IsOcpiEnabled}
                                  isDisabled={updatingLocationId === loc.Id}
                                  onChange={(e) => handleToggle(loc, e.target.checked)}
                                />
                              )}
                              {updatingLocationId === loc.Id ? (
                                <Spinner size="xs" color="#f57d36" />
                              ) : (
                                <Badge
                                  colorScheme={loc.IsOcpiEnabled ? "green" : "gray"}
                                  borderRadius="full"
                                >
                                  {loc.IsOcpiEnabled ? "Enabled" : "Disabled"}
                                </Badge>
                              )}
                            </Flex>
                          );
                        } else if (cell.column.Header === "Actions") {
                          cellData = (
                            <Flex align="center" justify="center" gap={2}>
                              <Tooltip label="View OCPI body" placement="top" hasArrow>
                                <IconButton
                                  icon={<Icon as={MdCode} />}
                                  colorScheme="blue"
                                  variant="ghost"
                                  size="sm"
                                  borderRadius="full"
                                  aria-label="View OCPI body"
                                  onClick={() =>
                                    handleViewBody("location", loc.Id, loc.Name || `Location ${loc.Id}`)
                                  }
                                />
                              </Tooltip>
                              <RoleBasedRender roles={onlyUAdmins}>
                                <Tooltip label="Broadcast to OCPI peers" placement="top" hasArrow>
                                  <IconButton
                                    icon={
                                      rowBroadcasting.has(`location-${loc.Id}`) ? (
                                        <Spinner size="xs" />
                                      ) : (
                                        <Icon as={FaTowerBroadcast} />
                                      )
                                    }
                                    size="sm"
                                    borderRadius="full"
                                    bg="#f57d36"
                                    color="white"
                                    _hover={{
                                      bg: "#e06a2a",
                                      transform: "translateY(-1px)",
                                    }}
                                    aria-label="Broadcast location"
                                    isDisabled={rowBroadcasting.has(`location-${loc.Id}`)}
                                    onClick={() => handleBroadcastRow("location", loc.Id)}
                                  />
                                </Tooltip>
                              </RoleBasedRender>
                            </Flex>
                          );
                        }
                        return (
                          <Td
                            {...cell.getCellProps()}
                            key={cellIdx}
                            py={4}
                            px={6}
                            borderColor={borderColor}
                            fontSize="sm"
                          >
                            {cellData}
                          </Td>
                        );
                      })}
                    </MotionTr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {renderPagination()}
        </>
      )}

      {/* Disable confirmation dialog */}
      <AlertDialog
        isOpen={confirmState.open}
        leastDestructiveRef={cancelRef}
        onClose={closeConfirm}
        isCentered
        motionPreset="slideInBottom"
      >
        <AlertDialogOverlay bg="rgba(0, 0, 0, 0.2)" backdropFilter="blur(12px)">
          <AlertDialogContent
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="16px"
            border="1px solid rgba(245, 125, 54, 0.4)"
            boxShadow="0 8px 32px rgba(245, 125, 54, 0.15)"
            p={3}
          >
            <AlertDialogHeader
              fontSize="xl"
              fontWeight="600"
              color="#333"
              pb={2}
              borderBottom="1px solid rgba(245, 125, 54, 0.2)"
            >
              <Flex align="center">
                <Icon as={MdLock} mr={2} color="#f57d36" boxSize={5} />
                Disable Location Roaming
              </Flex>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3} fontSize="md">
                Disabling OCPI for location{" "}
                <strong>
                  {confirmState.location?.Name || confirmState.location?.Id}
                </strong>{" "}
                will notify OCPI partners that its EVSEs are{" "}
                <strong>REMOVED</strong>, and will also stop pushing/pulling
                data for all of its stations until roaming is re-enabled.
              </Text>
              <Text fontWeight="500" textAlign="center">
                Do you want to proceed?
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter pt={3} borderTop="1px solid rgba(245, 125, 54, 0.2)">
              <Button
                ref={cancelRef}
                onClick={closeConfirm}
                variant="outline"
                borderColor="rgba(245, 125, 54, 0.5)"
                color="gray.600"
                borderRadius="8px"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                ml={3}
                bgGradient="linear(135deg, #f57d36 0%, #f35626 100%)"
                color="white"
                borderRadius="8px"
                fontWeight="600"
              >
                Disable
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </GlassMorphismCard>
  );
};

export default OcpiLocationsTable;
